import { sanitizeText, formatDate, normalizeImageUrl } from "./sanitize";
import type {
  StructuredDataBrand,
  StructuredDataOrganization,
  StructuredDataVariantProduct,
  StructuredDataAggregateRating,
  StructuredDataReview,
  StructuredDataInputProduct,
  StructuredDataInputStockPrice,
  StructuredDataInputRatings,
  StructuredDataInputReview,
} from "./types";

export function buildBrand(name: string): StructuredDataBrand {
  return {
    "@type": "Brand",
    name: sanitizeText(name),
  };
}

export function buildSeller(): StructuredDataOrganization {
  return {
    "@type": "Organization",
    name: "ASOS",
  };
}

export function buildSizeVariant(args: {
  name: string;
  sku: string;
  colour: string;
  imageUrl: string | null;
  brandName: string;
  ean?: string | null;
  size?: string | null;
  price: number;
  priceCurrency: string;
  isInStock: boolean;
}): StructuredDataVariantProduct {
  const variant: StructuredDataVariantProduct = {
    "@type": "Product",
    name: sanitizeText(args.name),
    sku: sanitizeText(args.sku),
    color: sanitizeText(args.colour),
    image: args.imageUrl,
    brand: buildBrand(args.brandName),
    offers: {
      "@type": "Offer",
      price: args.price,
      priceCurrency: sanitizeText(args.priceCurrency),
      availability: args.isInStock
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
      itemCondition: "https://schema.org/NewCondition",
      seller: buildSeller(),
    },
  };

  if (args.ean) {
    variant.gtin13 = sanitizeText(args.ean);
  }

  if (args.size) {
    variant.size = sanitizeText(args.size);
  }

  return variant;
}

export function buildSizeVariants(
  product: StructuredDataInputProduct | null | undefined,
  stockPriceVariants: StructuredDataInputStockPrice[] | null | undefined,
): StructuredDataVariantProduct[] {
  if (!product || !stockPriceVariants || product.isDiscontinued) {
    return [];
  }

  const primaryImage =
    product.images.find((img) => img.isPrimary) || product.images[0];
  const imageUrl = normalizeImageUrl(primaryImage?.url) || null;

  const stockPriceById = new Map(
    stockPriceVariants.map((sv) => [String(sv.id), sv]),
  );

  return product.variants
    .map((variant) => {
      const stockVariant = stockPriceById.get(String(variant.variantId));
      if (!stockVariant) return null;

      return buildSizeVariant({
        name: product.name,
        sku: variant.sku,
        colour: variant.colour,
        imageUrl,
        brandName: product.brandName,
        ean: variant.ean,
        size: variant.size,
        price: stockVariant.price.current.value,
        priceCurrency: stockVariant.price.currency,
        isInStock: stockVariant.isInStock,
      });
    })
    .filter((v): v is StructuredDataVariantProduct => v !== null);
}

export function buildAggregateRating(
  ratings: StructuredDataInputRatings | null | undefined,
): StructuredDataAggregateRating | null {
  if (!ratings) return null;

  const { averageOverallRating, totalReviewCount } = ratings;
  if (!averageOverallRating || !totalReviewCount) return null;

  return {
    "@type": "AggregateRating",
    ratingValue: averageOverallRating,
    ratingCount: totalReviewCount,
    bestRating: 5,
    worstRating: 1,
  };
}

function buildReview(review: StructuredDataInputReview): StructuredDataReview {
  const rating =
    typeof review.rating === "number"
      ? Math.max(1, Math.min(5, review.rating))
      : 1;
  const result: StructuredDataReview = {
    "@type": "Review",
    reviewRating: {
      "@type": "Rating",
      bestRating: 5,
      worstRating: 1,
      ratingValue: String(rating),
    },
    author: {
      "@type": "Person",
      name: sanitizeText(review.author || "Anonymous"),
    },
    name: sanitizeText(review.title || "No title provided"),
    reviewBody:
      sanitizeText(review.reviewText || "").substring(0, 300) ||
      "No review text provided",
  };

  const datePublished = formatDate(review.date ?? null);
  if (datePublished) {
    result.datePublished = datePublished;
  }

  return result;
}

export function buildReviews(
  reviews?: StructuredDataInputReview[],
): StructuredDataReview[] {
  if (!reviews) return [];

  return reviews
    .filter((r) => r !== null && r !== undefined && typeof r === "object")
    .slice(0, 3)
    .map(buildReview);
}
